import { useEffect, useMemo, useState } from 'react'

import { ChevronDown, Search } from 'lucide-react'

import { DashCard } from './DashboardCards'
import { scorecardRows, type ScorecardRow } from '../../../lib/district-read'
import { prettyName } from '../../../lib/mandal-name'

import type { MandalAggregate } from '../../../api/types'
import type { VerdictTone } from '../../../lib/health'

const SHOW_FIRST = 7
// Show the filter box only once the list is long enough to warrant scanning for one mandal.
const SEARCH_THRESHOLD = 8

const TONE_DOT: Record<VerdictTone, string> = { good: '#15803d', warn: '#b45309', bad: '#dc2626' }

const GRID = 'grid grid-cols-[minmax(0,1fr)_56px_minmax(64px,92px)_46px_50px_64px] items-center gap-2'

type SortKey = 'name' | 'health' | 'coverage' | 'openIssues' | 'openCritical' | 'overdueSites'

// First-click direction per column — the one that surfaces the "worst" rows first (lowest health /
// coverage, most open / critical / overdue); names go A→Z. A repeat click flips it.
const DEFAULT_DIR: Record<SortKey, 'asc' | 'desc'> = {
  name: 'asc',
  health: 'asc',
  coverage: 'asc',
  openIssues: 'desc',
  openCritical: 'desc',
  overdueSites: 'desc',
}

interface SortState {
  key: SortKey
  dir: 'asc' | 'desc'
}

function compareBy(a: ScorecardRow, b: ScorecardRow, key: SortKey): number {
  switch (key) {
    case 'name':
      return prettyName(a.name).localeCompare(prettyName(b.name))
    case 'coverage':
      // Null coverage (unsurveyed) sorts as the lowest — it IS the worst coverage for this read.
      return (a.coverage ?? -1) - (b.coverage ?? -1)
    default:
      return a[key] - b[key]
  }
}

/** Direction caret — space is always reserved (opacity toggles) so sorting never shifts the grid. */
function Caret({ active, dir }: { active: boolean; dir: 'asc' | 'desc' | undefined }) {
  return (
    <ChevronDown
      className={`h-[10px] w-[10px] flex-none transition-[transform,opacity] ${active ? 'opacity-100' : 'opacity-0'} ${active && dir === 'asc' ? 'rotate-180' : ''}`}
      aria-hidden
    />
  )
}

function ariaSortOf(active: boolean, dir: 'asc' | 'desc' | undefined): 'ascending' | 'descending' | 'none' {
  if (!active) {
    return 'none'
  }
  return dir === 'asc' ? 'ascending' : 'descending'
}

/** A sortable column header. */
function Th({ label, col, sort, onSort, align = 'left' }: {
  label: string
  col: SortKey
  sort: SortState | null
  onSort: (k: SortKey) => void
  align?: 'left' | 'right'
}) {
  const active = sort !== null && sort.key === col
  const dir = sort?.dir
  const right = align === 'right'
  const caret = <Caret active={active} dir={dir} />
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      title={`Sort by ${label.toLowerCase()}`}
      aria-sort={ariaSortOf(active, dir)}
      className={`flex items-center gap-[2px] text-[9.5px] font-bold tracking-[.06em] uppercase transition-colors focus-visible:ring-2 focus-visible:ring-blue-500/60 ${right ? 'justify-end' : ''} ${active ? 'text-gis-ink' : 'text-gis-heading hover:text-gis-ink'}`}
    >
      {right && caret}
      <span className="truncate">{label}</span>
      {!right && caret}
    </button>
  )
}

function Row({ r, active, onPick }: { r: ScorecardRow; active: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      title={`Scope the dashboard and map to ${prettyName(r.name)} (click again to clear)`}
      className={`${GRID} -mx-1 w-full cursor-pointer rounded-[8px] px-1 py-[4px] text-left transition-colors hover:bg-[#f5f7fa] focus-visible:ring-2 focus-visible:ring-blue-500/60 ${active ? 'bg-[#eef3fb]' : ''}`}
    >
      <span className="truncate text-[12px] font-semibold text-gis-ink-2">{prettyName(r.name)}</span>
      <span className="flex items-center gap-1.5" title={r.verdict.label}>
        <span className="h-[8px] w-[8px] flex-none rounded-full" style={{ background: TONE_DOT[r.verdict.tone] }} />
        <span className="text-[11.5px] font-bold tabular-nums text-gis-ink">{r.health}</span>
      </span>
      <span className="flex items-center gap-[6px]">
        <span className="flex h-[7px] min-w-0 flex-1 overflow-hidden rounded-full bg-gis-track-2">
          <span className="block h-full rounded-full bg-[#10b981]" style={{ width: `${r.coverage ?? 0}%` }} />
        </span>
        <span className="min-w-[28px] text-right text-[10.5px] font-semibold tabular-nums text-gis-ink-2">
          {r.coverage !== null ? `${r.coverage}%` : '—'}
        </span>
      </span>
      <span className="text-right text-[11.5px] font-bold tabular-nums text-gis-ink">{r.openIssues}</span>
      <span className={`text-right text-[11.5px] font-bold tabular-nums ${r.openCritical > 0 ? 'text-gis-red' : 'text-gis-muted'}`}>
        {r.openCritical}
      </span>
      <span className={`text-right text-[11.5px] font-bold tabular-nums ${r.overdueSites > 0 ? 'text-[#b45309]' : 'text-gis-muted'}`}>
        {r.overdueSites}
      </span>
    </button>
  )
}

interface DistrictScorecardProps {
  mandals: MandalAggregate[]
  activeMandals: readonly string[]
  /** Click a row → toggle that mandal into the page scope (dashboard + map re-scope in place). */
  onPickMandal: (name: string) => void
}

/**
 * The DISTRICT SCORECARD — the league table that fuses both boards per mandal: transparent health
 * (dot = verdict) · coverage bar · open · critical · overdue sites. The review-meeting artifact.
 *
 * Efficient + sortable: `scorecardRows` runs once per mandal set (memoised), then a memoised
 * filter → sort → slice pipeline drives the view. Any column header sorts (worst-first on first
 * click, toggles direction after); the default is the curated worst-health-first order. A filter box
 * appears once the list is long. Clicking a row scopes the whole page to that mandal.
 */
export function DistrictScorecard({ mandals, activeMandals, onPickMandal }: DistrictScorecardProps) {
  const baseRows = useMemo(() => scorecardRows(mandals), [mandals])
  const [expanded, setExpanded] = useState(false)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<SortState | null>(null)

  // A new scope changes the row set — collapse + clear the filter so the worst always leads the read.
  useEffect(() => {
    setExpanded(false)
    setQuery('')
  }, [mandals])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q === '') {
      return baseRows
    }
    return baseRows.filter((r) => prettyName(r.name).toLowerCase().includes(q))
  }, [baseRows, query])

  const sorted = useMemo(() => {
    if (sort === null) {
      return filtered // default: the curated worst-health-first order from scorecardRows
    }
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => compareBy(a, b, sort.key) * dir)
  }, [filtered, sort])

  const visible = expanded ? sorted : sorted.slice(0, SHOW_FIRST)

  const onSort = (key: SortKey): void =>
    setSort((prev) =>
      prev === null || prev.key !== key
        ? { key, dir: DEFAULT_DIR[key] }
        : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' },
    )

  return (
    <DashCard title="District scorecard" caption="click a header to sort · a row to scope">
      {baseRows.length === 0 ? (
        <div className="py-6 text-center text-[11.5px] text-gis-soft">No mandals in this selection.</div>
      ) : (
        <>
          {baseRows.length > SEARCH_THRESHOLD && (
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute top-1/2 left-2 h-[13px] w-[13px] -translate-y-1/2 text-gis-soft" aria-hidden />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter mandals…"
                aria-label="Filter mandals"
                className="w-full rounded-[8px] border border-gis-line bg-white py-[5px] pr-2 pl-7 text-[12px] text-gis-ink placeholder:text-gis-soft focus:border-blue-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
              />
            </div>
          )}

          <div className={`${GRID} border-b border-gis-line-2 px-0 pb-[5px]`}>
            <Th label="Mandal" col="name" sort={sort} onSort={onSort} />
            <Th label="Health" col="health" sort={sort} onSort={onSort} />
            <Th label="Coverage" col="coverage" sort={sort} onSort={onSort} />
            <Th label="Open" col="openIssues" sort={sort} onSort={onSort} align="right" />
            <Th label="Crit" col="openCritical" sort={sort} onSort={onSort} align="right" />
            <Th label="Overdue" col="overdueSites" sort={sort} onSort={onSort} align="right" />
          </div>

          {sorted.length === 0 ? (
            <div className="py-5 text-center text-[11.5px] text-gis-soft">No mandals match “{query.trim()}”.</div>
          ) : (
            <>
              <div className="flex flex-col pt-1">
                {visible.map((r) => (
                  <Row key={r.name} r={r} active={activeMandals.includes(r.name)} onPick={() => onPickMandal(r.name)} />
                ))}
              </div>
              {sorted.length > SHOW_FIRST && (
                <button
                  type="button"
                  onClick={() => setExpanded((e) => !e)}
                  className="mt-1 flex w-full items-center justify-center gap-1 rounded-[8px] py-[5px] text-[11px] font-semibold text-gis-heading transition-colors hover:bg-gis-track focus-visible:ring-2 focus-visible:ring-blue-500/60"
                >
                  <ChevronDown className={`h-[13px] w-[13px] transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden />
                  {expanded ? 'Show fewer' : `Show all ${sorted.length} mandals`}
                </button>
              )}
            </>
          )}
        </>
      )}
    </DashCard>
  )
}
